import React from 'react';
import { AuthContext } from 'contexts';
import { Empty } from 'components';
import { featureEnabled, getFlagValue } from 'utils';
import { getTableauToken } from 'api/TableauController';
import { createRoot } from 'react-dom/client';
import styled from 'styled-components';
import { useDynamicModule } from './hooks';

const TABLEAU_EMBEDDING_API_URL =
  'https://embedding.tableauusercontent.com/tableau.embedding.3.6.0.min.js';

const emptyRoots = new WeakMap<Element, ReturnType<typeof createRoot>>();

const renderEmptyState = (container: Element | null) => {
  if (!container) return;
  let root = emptyRoots.get(container);
  if (!root) {
    root = createRoot(container);
    emptyRoots.set(container, root);
  }
  root.render(<Empty />);
};

const MarketInsight: React.FC = () => {
  const { auth } = React.useContext(AuthContext);
  const [viewLoaded, setViewLoaded] = React.useState(false);
  const [tableauToken, setTableauToken] = React.useState<string | null>(null);
  const tableauContainerRef = React.useRef<HTMLDivElement>(null);
  const tableauVizRef = React.useRef<any>(null);

  // Dynamically load tableau module since there's no official npm package
  const tableau = useDynamicModule(TABLEAU_EMBEDDING_API_URL);
  const {
    TableauViz,
    TableauEventType,
    PrintOrientation,
    PrintPageSize,
    PrintScaling,
  }: any = tableau || {};

  React.useEffect(() => {
    (async () => {
      try {
        const { token } = await getTableauToken();
        setTableauToken(token);
      } catch (error) {
        console.error('Error fetching Tableau token:', error);
        renderEmptyState(tableauContainerRef.current);
      }
    })();
  }, []);

  React.useEffect(() => {
    const embedTableau = () => {
      try {
        const url = featureEnabled('analyticsTableauUrlV2Feature')
          ? process.env.TABLEAU_EMBED_URL_TEST
          : process.env.TABLEAU_EMBED_URL;
        const newViz = new TableauViz();
        newViz.src = url;
        newViz.token = tableauToken;
        newViz.toolbar = 'hidden';
        newViz.id = 'tableauViz';

        const firstInteractiveHandler = () => setViewLoaded(true);
        newViz.addEventListener(
          TableauEventType.FirstInteractive,
          firstInteractiveHandler,
        );

        if (tableauContainerRef.current) {
          tableauContainerRef.current.innerHTML = '';
          tableauContainerRef.current.appendChild(newViz);
          tableauVizRef.current = newViz;
        }
        return () =>
          newViz.removeEventListener(
            TableauEventType.FirstInteractive,
            firstInteractiveHandler,
          );
      } catch (error) {
        console.error('Error embedding Tableau:', error);
        tableauVizRef.current = null;
        renderEmptyState(tableauContainerRef.current);
      }
    };
    if (TableauViz && tableauToken) return embedTableau();
  }, [TableauViz, tableauToken]);

  const tableauForBB =
    featureEnabled('analyticsTableauFeaturePhase', 1) &&
    auth?.org?.id &&
    auth.org.id === process.env.BROKERBAY_ORG_ID;

  const hasAnalyticsTableauFeaturePhase2 = featureEnabled(
    'analyticsTableauFeaturePhase',
    2,
  );

  const tableauForMLS =
    hasAnalyticsTableauFeaturePhase2 &&
    auth?.org?.onboardingPortalId &&
    (process.env.TABLEAU_ANALYTICS_PORTAL_ID?.split(',') || []).includes(
      auth.org.onboardingPortalId.toString(),
    );
  const tableauForTestMlsBB =
    !featureEnabled('analyticsTableauTestMlsFeature', 'off') &&
    auth?.assumingUserId &&
    !auth.isAssumedNotBB &&
    auth?.org?.onboardingPortalId &&
    (process.env.TABLEAU_ANALYTICS_PORTAL_ID_TEST?.split(',') || []).includes(
      auth.org.onboardingPortalId.toString(),
    );
  const analyticsTableauTestMlsFeatureIndexes = getFlagValue(
    'analyticsTableauTestMlsFeature',
  );
  const analyticsTableauTestMlsFeatureOffOrBb =
    featureEnabled('analyticsTableauTestMlsFeature', 'off') ||
    featureEnabled('analyticsTableauTestMlsFeature', 'bb');
  const tableauForTestMlsClients =
    auth?.org?.onboardingPortalId &&
    !analyticsTableauTestMlsFeatureOffOrBb &&
    process.env.TABLEAU_ANALYTICS_PORTAL_ID_TEST &&
    analyticsTableauTestMlsFeatureIndexes.split(',').some(onIndex => {
      try {
        const portalIds =
          process.env.TABLEAU_ANALYTICS_PORTAL_ID_TEST!.split(',');
        const index = parseInt(onIndex, 10) - 1;
        return portalIds[index] === auth.org.onboardingPortalId;
      } catch {
        return false;
      }
    });

  return (
    <>
      {/* HACK: perfectly covers up the original download button to overwrite the behaviour
          Must update the position of the button if the layout of the worksheet changes */}
      {viewLoaded && (
        <PdfExportOverwriteButton
          onClick={() => {
            const activeSheetName =
              tableauVizRef.current?.workbook?.activeSheet?.name;
            tableauVizRef.current?.exportPDFAsync([activeSheetName], {
              orientation: PrintOrientation.Portrait,
              pageSize: PrintPageSize.Letter,
              scaling: PrintScaling.AtMost2PagesHigh,
            });
          }}
        />
      )}
      <StyledTableauContainer ref={tableauContainerRef} />
    </>
  );
};

export default MarketInsight;

const vizLeftMargin = (offset = 0) =>
  `max(calc((100% - 825px) / 2 + ${offset}px), ${offset}px)`;

const StyledTableauContainer = styled.div`
  margin-left: ${vizLeftMargin()};
  width: 825px;
`;

const PdfExportOverwriteButton = styled.button`
  position: absolute;
  top: 29px;
  left: ${vizLeftMargin(322)};
  width: 36px;
  height: 32px;
  z-index: 1;
  &:hover {
    background: rgba(255, 255, 255, 0.5);
  }
`;
